import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Content, FlexboxGrid, Form, Text, Row, Col } from 'rsuite';
import PortalHeader from '../../components/layout/PortalHeader';
import FormTextField from '../../components/form/FormTextField';
import { useDispatch, useSelector } from 'react-redux';
import {
  retrieveCognitoUserAsync,
  terminateSession,
  validateCognitoEmailAddress,
} from '../../store/slices/auth/authThunks';
import {
  selectIsAuthCodeRequired,
  selectWorkStarted,
  selectWorkSuccess,
} from '../../store/slices/auth/authSelectors';
import IconIndicator from '../../components/user-feedback/IconIndicator';
import Notice from '../../components/user-feedback/Notice';
import {
  logout,
  resetWorkProgressState,
} from '../../store/slices/auth/authSlice';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import { FLOWS_ENUM } from '../../common/enums';
import SchemaTyped from 'rsuite/esm/Schema/Schema';
import { clearPersistedState } from '../../common/tokenPersistence';
import AuthCodeModal from '../../components/auth/AuthCodeModal';
import { ERROR_EMAIL } from '../../components/form/constants';

const EmailCheck = () => {
  const form = useRef();
  const dispatch = useDispatch();
  const placeNavigate = usePlaceNavigate();
  const [formValues, setFormValues] = useState({});
  const [formError, setFormError] = useState({});
  const [isUserExists, setIsUserExists] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const isCheckingEmail = useSelector(selectWorkStarted);
  const showAuthModal = useSelector(selectIsAuthCodeRequired);

  const loginSuccess = useSelector(selectWorkSuccess);

  const formModel = useMemo(() => {
    return {
      email: '',
    };
  }, []);

  const { StringType } = SchemaTyped.Types;

  const validationModel = {
    email: StringType()
      .isEmail(ERROR_EMAIL)
      .isRequired('Enter your email address'),
  };

  useEffect(() => {
    setFormValues(formModel);

    return () => {
      dispatch(resetWorkProgressState());
    };
  }, [formModel, setFormValues, dispatch]);

  useEffect(() => {
    if (loginSuccess === true) {
      setIsUserExists(false);
      placeNavigate('/dashboard');
      dispatch(resetWorkProgressState());
    }
  }, [loginSuccess, dispatch, placeNavigate]);

  const onNextClicked = async () => {
    if (!form.current.check()) return false;
    setIsUserExists(false);
    let emailAddress = formValues['email'];
    let result = await dispatch(validateCognitoEmailAddress(emailAddress));
    if (result.success) {
      dispatch(resetWorkProgressState());
      return result.success;
    } else {
      if (!result.validationError && result.userExists) {
        setIsUserExists(true);
      }
    }
  };

  const onLoginModalReject = () => {
    setIsLoggingIn(false);
    dispatch(logout());
    dispatch(terminateSession());
    clearPersistedState();
  };

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader
          flow={FLOWS_ENUM.CREATE_ACCOUNT}
          showAccountDropdown={false}
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content as="div">
          <FlowPageLayout
            title="Email Address"
            onNext={onNextClicked}
          >
            <Form
              fluid
              ref={form}
              checkTrigger="blur"
              formDefaultValue={formModel}
              onCheck={setFormError}
              onChange={setFormValues}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            >
              <FlexboxGrid justify="center">
                <FlexboxGrid.Item
                  role="grid"
                  colspan={24}
                >
                  <Row className="mb-24">
                    <Col xs={24}>
                      <Text>
                        Let&apos;s get started by entering your email address.
                        We will send a code to this email address to verify that
                        this is your real email address, so please make sure
                        that this is a valid email address, and that you have
                        access to this email address at all times. Please choose
                        an email address that can be used for the duration of
                        this license. For property managers, please use a
                        company email address (not an employee&apos;s email
                        address) that you will have access to for your
                        company&apos;s duration, as email addresses cannot be
                        updated for your account at this time.
                      </Text>
                    </Col>
                  </Row>
                  <Row>
                    <Col
                      xs={24}
                      lg={12}
                    >
                      <FormTextField
                        name="email"
                        label="Email Address"
                        validationRule={validationModel.email}
                        hasError={formError['email']}
                        errorMessage="An email address is required."
                        className="full-width"
                        inputFieldType="email"
                        autoComplete="email"
                      />
                    </Col>
                  </Row>
                  {isCheckingEmail && !isUserExists && (
                    <Row className="mt-24">
                      <Col xs={24}>
                        <IconIndicator
                          type="loading"
                          color="#A7A7A7"
                          text="Checking email..."
                          isVertical={false}
                        />
                      </Col>
                    </Row>
                  )}
                  {isUserExists && !isLoggingIn && (
                    <Row className="mt-24">
                      <Col xs={24}>
                        <Notice
                          type="warning"
                          message="A user account already exists for this email address."
                          actionComponent={
                            <Button
                              className="btn-secondary mr-8"
                              onClick={async () => {
                                dispatch(resetWorkProgressState());
                                setIsLoggingIn(true);
                                await dispatch(
                                  retrieveCognitoUserAsync(formValues['email'])
                                );
                              }}
                            >
                              Login
                            </Button>
                          }
                        />
                      </Col>
                    </Row>
                  )}
                  {isLoggingIn && (
                    <Row className="mt-24">
                      <Col xs={24}>
                        <IconIndicator
                          type="loading"
                          color="#A7A7A7"
                          text="Logging in..."
                          isVertical={false}
                        />
                      </Col>
                    </Row>
                  )}
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </Form>
          </FlowPageLayout>
        </Content>
        <AuthCodeModal
          showModal={showAuthModal}
          onModalReject={onLoginModalReject}
        />
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default EmailCheck;
